import { Button, DatesSeparator } from '@koolumbus/web/ui';
import { createTxs, useLocale, useToggle } from '@koolumbus/web/utils';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { Reducer, useEffect, useReducer, useState } from 'react';
import shallow from 'zustand/shallow';
import { useAuthState } from '../../../../lib/context/auth.context';
import date, { formatDate } from '../../../../lib/helpers/date';
import { PropertyData } from '../../../../lib/models';
import { DatesRange, useSearchState } from '../../../../lib/state/search-state.store';
import { QueryOutput, trpc } from '../../../../lib/trpc';
import { ButtonLoader } from '../../../common/Loader';
import AuthModal from '../../../common/auth-modal/AuthModal';
import DatesPicker from '../../../common/dates-picker/DatesPicker';
import FloatingCardContent from './FloatingCardContent';

interface NewBookingContentProps {
  property: PropertyData;
}

const NewBookingContent: React.FC<React.PropsWithChildren<NewBookingContentProps>> = ({
  property: {
    id: propertyId,
    roomTypeId,
    cleaningCostMedium,
    details: { pricePerMonth, deposit },
  },
}) => {
  const tx = useTxs();

  const [extraCostsVisible, { toggle: toggleExtraCosts }] = useToggle(false);

  return (
    <FloatingCardContent
      top={
        <>
          <div className={clsx('flex flex-col', extraCostsVisible && 'space-y-2.5 py-3')}>
            <p className="text-gray-500 text-lg lg:text-xl">
              €{' '}
              <span className="text-gray-800 text-xl sm:text-2xl font-semibold sm:font-bold">
                {pricePerMonth}
              </span>{' '}
              / {tx.month}
            </p>

            {extraCostsVisible && (
              <div className="flex flex-col pl-0.5 text-[13px] space-y-0.5 text-gray-500">
                <span>
                  {tx.deposit}: € {deposit}
                </span>
                <span>
                  {tx.adminCosts}: € {cleaningCostMedium ?? '-'}
                </span>
              </div>
            )}
          </div>

          <button
            onClick={toggleExtraCosts}
            className={clsx(
              'flex lg:hidden flex-col text-gray-500 text-sm text-right underline',
              extraCostsVisible && 'h-full pt-4',
            )}
          >
            {tx.extraCosts(extraCostsVisible)}
          </button>
        </>
      }
      center={
        <div className="flex flex-col w-full pl-2 space-y-1 text-gray-500">
          <span>
            {tx.deposit}: € {deposit}
          </span>
          <span>
            {tx.adminCosts}: € {cleaningCostMedium ?? '-'}
          </span>
        </div>
      }
      bottom={<NewBookingDatesSection roomTypeId={roomTypeId} propertyId={propertyId} />}
    />
  );
};

interface NewBookingDatesSectionProps {
  propertyId: string;
  roomTypeId: string;
}

type DatesSectionState =
  | { status: 'loading' }
  | { status: 'no_dates' }
  | { status: 'unavailable' }
  | { status: 'available'; dates: DatesRange };

type DatesSectionStateAction =
  | {
      type: 'SET';
      available: boolean;
      dates: DatesRange;
    }
  | {
      type: 'NO_DATES';
    };

const datesSectionStateReducer: Reducer<DatesSectionState, DatesSectionStateAction> = (
  _state,
  action,
) => {
  if (action.type === 'NO_DATES') {
    return {
      status: 'no_dates',
    };
  }

  if (action.available) {
    return {
      status: 'available',
      dates: action.dates,
    };
  }

  return {
    status: 'unavailable',
  };
};

const NewBookingDatesSection: React.FC<React.PropsWithChildren<NewBookingDatesSectionProps>> = ({
  propertyId,
  roomTypeId,
}) => {
  const { client: api } = trpc.useUtils();
  const locale = useLocale();
  const tx = useTxs();
  const router = useRouter();
  const { status: authStatus } = useAuthState();

  const searchState = useSearchState(
    (store) => ({
      ready: store.ready,
      dates: store.state.dates,
    }),
    shallow,
  );

  const [state, dispatch] = useReducer(datesSectionStateReducer, { status: 'loading' });
  const [bookings, setBookings] = useState<QueryOutput['properties']['availability']['bookings']>();

  const [isAuthModalOpen, { on: openAuthModal, off: closeAuthModal }] = useToggle();

  useEffect(() => {
    const { ready, dates } = searchState;

    if (!ready) {
      return;
    }

    api.properties.availability
      .query({
        roomTypeId,
        exact: false,
        dates: dates ? { from: dates.dateFrom, to: dates.dateTo } : undefined,
      })
      .then((res) => {
        setBookings(res.bookings);

        if (dates) {
          const { available, availableFrom, availableTo } = res;

          const dateFrom = availableFrom ?? dates.dateFrom;
          const dateTo = availableTo ?? dates.dateTo;

          dispatch({ type: 'SET', available: available!, dates: { dateFrom, dateTo } });
        } else {
          dispatch({ type: 'NO_DATES' });
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchState.ready]);

  const onChangeDates = (value: DatesRange | undefined) => {
    if (value) {
      dispatch({
        type: 'SET',
        available: true,
        dates: value,
      });
    }
  };

  const onBook = () => {
    if (authStatus !== 'loggedIn') {
      openAuthModal();
    } else {
      if (state.status !== 'available') {
        return;
      }

      router.push({
        pathname: `/property/${propertyId}/book`,
        query: {
          dateFrom: date(state.dates.dateFrom).format('YYYY-MM-DD'),
          dateTo: date(state.dates.dateTo).format('YYYY-MM-DD'),
        },
      });
    }
  };

  const btnClass = 'w-full text-sm h-10 sm:font-medium';

  if (state.status === 'loading') {
    return (
      <Button isDisabled onClick={() => {}} className={btnClass}>
        <ButtonLoader />
      </Button>
    );
  }

  if (state.status === 'unavailable' || state.status === 'no_dates') {
    return (
      <DatesPicker
        value={undefined}
        onChange={onChangeDates}
        unavailableRanges={bookings}
        isResettable={false}
        renderInput={({ openPicker }) => (
          <Button onClick={openPicker} className={btnClass}>
            {tx.checkAvailability}
          </Button>
        )}
      />
    );
  }

  return (
    <>
      <div
        className={clsx(
          'flex flex-col w-full space-y-2',
          'sm:flex-row lg:flex-col sm:space-y-0 lg:space-y-2 sm:space-x-2 lg:space-x-0',
        )}
      >
        <DatesPicker
          value={state.dates}
          onChange={onChangeDates}
          unavailableRanges={bookings}
          isResettable={false}
          renderInput={({ openPicker }) => (
            <button
              type="button"
              onClick={openPicker}
              className={clsx(btnClass, 'bg-gray-200 font-medium hover:bg-gray-300 rounded')}
            >
              <div className="flex justify-center items-center text-gray-500">
                <span className="text-gray-700 ml-2">
                  {formatDate(state.dates.dateFrom, locale)}
                </span>
                <span className="px-2">
                  <DatesSeparator />
                </span>
                <span className="text-gray-700">{formatDate(state.dates.dateTo, locale)}</span>
              </div>
            </button>
          )}
        />

        <Button onClick={onBook} size="lg" className={btnClass}>
          {tx.book}
        </Button>
      </div>

      {authStatus !== 'loggedIn' && (
        <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} onLoggedIn={onBook} />
      )}
    </>
  );
};

const useTxs = createTxs({
  en: {
    month: 'month',
    extraCosts: (visible: boolean) => `${visible ? 'Hide' : 'Show'} extra costs`,
    deposit: 'Deposit',
    adminCosts: 'Administrative costs',
    checkAvailability: 'CHECK AVAILABILITY',
    book: 'BOOK',
  },
  it: {
    month: 'mese',
    extraCosts: (visible: boolean) => `${visible ? 'Nascondi' : 'Mostra'} costi extra`,
    deposit: 'Deposito',
    adminCosts: 'Costi amministrativi',
    checkAvailability: 'VERIFICA DISPONIBILITÀ',
    book: 'PRENOTA',
  },
});

export default NewBookingContent;
